import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import TrendBadge from "./TrendBadge";
import Sparkline from "./Sparkline";
import { fmtNum, statusClass } from "../lib/format";

/**
 * ControlTrendSheet — slide-over panel revealed on ControlCard click.
 *
 * Shows the WoW trend, larger 8-week sparkline, healthy/unhealthy/missing
 * breakdown, owner/sync metadata, and a CTA to open the endpoint inventory.
 */
export default function ControlTrendSheet({ open, onOpenChange, control }) {
  const nav = useNavigate();
  if (!control) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[560px]" />
      </Sheet>
    );
  }
  const c = control;
  const tone = c.status === "Compliant" ? "var(--cv-green)" : c.status === "At risk" ? "#E5C243" : "var(--cv-crimson)";
  const trackBg = c.status === "Compliant" ? "rgba(62,124,79,0.18)" : c.status === "At risk" ? "rgba(229,194,67,0.25)" : "rgba(165,48,15,0.18)";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[580px] overflow-y-auto" data-testid="control-trend-sheet">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="font-serif-display text-[26px] leading-tight" style={{ color: "var(--cv-crimson)" }}>
              {c.control}
            </SheetTitle>
            <span className={`status-pill ${statusClass(c.status)}`}>{c.status}</span>
          </div>
          <SheetDescription className="text-[13.5px]">
            {c.vendor} · Owner <strong>{c.owner}</strong> · Synced {c.synced_min_ago} min ago
          </SheetDescription>
        </SheetHeader>

        {/* Headline metric + trend */}
        <div className="cv-panel mt-4" style={{ borderLeft: `4px solid ${tone}` }} data-testid="sheet-headline">
          <div className="flex items-center justify-between">
            <div className="cv-kpi-label">Healthy % · current</div>
            {c.trend && <TrendBadge trend={c.trend} testid="sheet-trend" />}
          </div>
          <div className="flex items-end gap-2 mt-2">
            <div className="font-serif-display leading-none" style={{ color: tone, fontSize: 56 }}>
              {c.health_pct}%
            </div>
            <div className="pb-2 text-[12.5px] small-caps" style={{ color: "var(--cv-charcoal-soft)" }}>
              In-scope <strong style={{ color: "var(--cv-charcoal)" }}>{fmtNum(c.in_scope)}</strong>
            </div>
          </div>
          <div
            className="relative h-2 w-full overflow-hidden rounded-full mt-3"
            style={{ background: trackBg }}
          >
            <div className="h-full" style={{ width: `${Math.min(100, c.health_pct)}%`, background: tone }} />
          </div>
        </div>

        {/* Sparkline panel */}
        <div className="cv-panel mt-3" data-testid="sheet-spark-panel">
          <div className="text-[12.5px] small-caps font-bold mb-2" style={{ color: "var(--cv-charcoal-soft)" }}>
            8-week healthy % trajectory
          </div>
          <Sparkline data={c.sparkline} color={tone} height={140} testid="sheet-spark" />
        </div>

        {/* Healthy / Unhealthy / Missing breakdown */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="cv-tile cv-tile-green" style={{ cursor: "default" }} data-testid="sheet-healthy">
            <div className="cv-tile-label">Healthy</div>
            <div className="cv-tile-val">{fmtNum(c.healthy)}</div>
            <div className="cv-tile-sub">agents reporting clean</div>
          </div>
          <div className="cv-tile cv-tile-amber" style={{ cursor: "default" }} data-testid="sheet-unhealthy">
            <div className="cv-tile-label">Unhealthy</div>
            <div className="cv-tile-val">{fmtNum(c.unhealthy)}</div>
            <div className="cv-tile-sub">installed but degraded</div>
          </div>
          <div className="cv-tile cv-tile-red" style={{ cursor: "default" }} data-testid="sheet-missing">
            <div className="cv-tile-label">Missing</div>
            <div className="cv-tile-val">{fmtNum(c.missing)}</div>
            <div className="cv-tile-sub">no agent installed</div>
          </div>
        </div>

        {/* CTA */}
        <Button
          data-testid="sheet-view-endpoints"
          className="w-full mt-4"
          style={{ background: "var(--cv-crimson)" }}
          onClick={() => {
            onOpenChange(false);
            nav(`/drill/control_endpoints?control=${encodeURIComponent(c.control)}`);
          }}
        >
          View endpoint inventory <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </SheetContent>
    </Sheet>
  );
}
